import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import { selectIsAuthenticated } from '@paradigm/store/auth';

import {
  FEATURE_BLOCK_TERMINAL,
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

import BlockTerminalTab from '#/components/Terminal/BlockTerminalTab';
import {
  SIDE_PANEL_MAX_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
} from '#/components/Terminal/layout-constants';

/**
 * Right-rail mount of the Block Terminal. Renders only when the user has
 * pinned the terminal as a side panel and the drawer is open. Both the
 * bottom-panel tabs and the side panel are visible simultaneously, similar
 * to the Paradex layout.
 *
 * The panel has a single outer width, resized by dragging its left edge and
 * persisted per-user. The terminal package manages its own internal layout
 * (sessions sidebar, dividers) inside the panel.
 */
export default function BlockTerminalSidePanel() {
  const enabled = useIsFeatureFlagEnabled(FEATURE_BLOCK_TERMINAL);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth: drawerState.sidePanelWidth,
        currentWidth: drawerState.sidePanelWidth,
      };
      setIsDragging(true);
    },
    [drawerState.sidePanelWidth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      // Defensive: if no mouse button is held the drag has effectively
      // ended without a paired pointerup (release outside the window,
      // lost pointer-capture, OS interrupt). Without this guard, bare
      // hover over the handle keeps resizing because dragRef stays set
      // from the previous gesture. `e.buttons` is the bitmask of
      // currently-pressed buttons (0 = none).
      if (e.buttons === 0) {
        dragRef.current = null;
        setIsDragging(false);
        return;
      }
      // Dragging left (clientX decreasing) grows the panel.
      const next = clamp(
        r.startWidth - (e.clientX - r.startX),
        SIDE_PANEL_MIN_WIDTH,
        SIDE_PANEL_MAX_WIDTH,
      );
      r.currentWidth = next;
      drawerActions.setSidePanelWidth(next);
    },
    [drawerActions],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released — ignore
      }
      // Persist once at end of drag — pointer-move only wrote to
      // in-memory state to avoid 60-writes-per-second to localStorage.
      drawerActions.commitSidePanelWidth(r.currentWidth);
      dragRef.current = null;
      setIsDragging(false);
    },
    [drawerActions],
  );

  if (!isAuthenticated) return null;
  if (!enabled) return null;
  if (drawerState.pinMode !== 'side-panel') return null;
  if (!drawerState.isOpen) return null;

  return (
    <Panel $width={drawerState.sidePanelWidth} $dragging={isDragging}>
      <LeftEdgeHandle
        $dragging={isDragging}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label="Resize terminal side panel"
        role="separator"
        aria-orientation="vertical"
      />
      <BlockTerminalTab />
    </Panel>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const Panel = styled.aside<{
  readonly $width: number;
  readonly $dragging: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => $width}px;
  max-width: 100vw;
  background: var(--bg-color-container, #0e131a);
  border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  display: flex;
  flex-direction: column;
  z-index: 900;
  /* No drop-shadow — the LayoutContainer shrinks the main app by the
     panel's exact width, so the panel sits flush against the main view
     instead of floating above it. A shadow here would only reinforce the
     "modal-over-content" feel we are explicitly moving away from. */
  /* Disable the width animation during a drag so the panel tracks the
     cursor 1:1 instead of lagging behind it. */
  transition: ${({ $dragging }) => ($dragging ? 'none' : 'width 0.18s ease')};
`;

const LeftEdgeHandle = styled.div<{ readonly $dragging: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 6px;
  margin-left: -3px;
  cursor: ew-resize;
  touch-action: none;
  z-index: 1;
  transition: background-color 0.2s ease;
  background-color: ${({ $dragging }) =>
    $dragging ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;
